/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { AxiosInstance } from 'axios';

import contentDisposition from 'content-disposition';
import { saveAs } from 'file-saver';
import { CommonAPI } from '../common/responses';
import { DocumentsAPI } from '../common/documents';
import { baseUrl, NotaryAPI } from './types';
import { FilterParams } from '@shared/types/pipeline';

export const roomsClient = (request: AxiosInstance) => ({
  async fetchAssignedRooms(limit: number, offset: number, filter: FilterParams): Promise<NotaryAPI.RoomListResponse> {
    return (await request.post(`${baseUrl}/rooms/assigned`, { filter }, { params: { limit, offset } })).data;
  },

  async fetchRoomDetail(roomId: string): Promise<NotaryAPI.RoomDetailResponse> {
    return (await request.get(`${baseUrl}/rooms/${roomId}`)).data;
  },

  async fetchRooms(limit: number, offset: number, filter: FilterParams): Promise<NotaryAPI.RoomListResponse> {
    return (await request.post(`${baseUrl}/rooms`, { filter }, { params: { limit, offset } })).data;
  },

  async assignRoom(roomId: string): Promise<void> {
    await request.patch(`${baseUrl}/rooms/${roomId}/assign`, null);
  },

  async unassignRoom(roomId: string, notaryId: string): Promise<void> {
    await request.patch(`${baseUrl}/rooms/${roomId}/unassign`, { notaryId });
  },

  async fetchCallDetailsForRoom(roomId: string): Promise<NotaryAPI.CallDetailsResponse> {
    return (await request.get(`${baseUrl}/rooms/${roomId}/call`)).data;
  },

  async fetchParticipantsInRoom(roomId: string): Promise<NotaryAPI.RoomParticipantsResponse> {
    return (await request.get(`${baseUrl}/rooms/${roomId}/participants`)).data;
  },

  async fetchPendingActions(): Promise<NotaryAPI.PendingActionResponse> {
    return (await request.get(`${baseUrl}/rooms/pending-actions`)).data;
  },

  async createParticipant(roomId: string, participant: NotaryAPI.CreateParticipantRequestBody) {
    await request.post(`${baseUrl}/rooms/${roomId}/participants`, participant);
  },

  async patchParticipant(
    roomId: string,
    participantId: string,
    patchReq: NotaryAPI.PatchParticipantRequestBody,
  ): Promise<NotaryAPI.Participant> {
    return (await request.patch(`${baseUrl}/rooms/${roomId}/participants/${participantId}`, patchReq)).data;
  },

  async deleteParticipant(roomId: string, participantId: string): Promise<void> {
    await request.delete(`${baseUrl}/rooms/${roomId}/participants/${participantId}`);
  },

  async resendParticipantInvite(roomId: string, participantId: string): Promise<void> {
    await request.post(`${baseUrl}/rooms/${roomId}/participants/${participantId}/resend-invite`);
  },

  async scheduleCall(roomId: string, req: NotaryAPI.ScheduleCallRequestBody) {
    await request.post(`${baseUrl}/rooms/${roomId}/schedule-call`, req);
  },

  async prepareAttendAppointment(roomId: string): Promise<void> {
    await request.get(`${baseUrl}/rooms/${roomId}/prepare-attend-appointment`);
  },

  async fetchRoomEvents(roomId: string): Promise<CommonAPI.RoomEvent[]> {
    return (await request.get(`${baseUrl}/rooms/${roomId}/events`)).data;
  },

  async fetchRoomEventsPdf(roomId: string): Promise<void> {
    const res = await request.get(`${baseUrl}/rooms/${roomId}/events/pdf`, {
      responseType: 'blob',
    });
    const cd = contentDisposition.parse(res.headers['content-disposition'] as string);
    const file = new Blob([res.data], {
      type: 'application/pdf',
    });
    saveAs(file, cd.parameters.filename);
  },

  async setIdvProviderOverride(roomId: string, provider: string): Promise<void> {
    await request.patch(`${baseUrl}/rooms/${roomId}/idv-override`, { provider });
  },

  async setVideoProviderOverride(roomId: string, provider: string): Promise<void> {
    await request.patch(`${baseUrl}/rooms/${roomId}/video-override`, { provider });
  },

  async getRecordings(roomId: string): Promise<NotaryAPI.Recording[]> {
    return (await request.get(`${baseUrl}/rooms/${roomId}/recordings`)).data;
  },

  async getAllOrganizations(): Promise<NotaryAPI.OrganizationsResponse> {
    return (await request.get(`${baseUrl}/rooms/organizations/all`)).data;
  },

  async getAllGroups(): Promise<NotaryAPI.GroupsResponse> {
    return (await request.get(`${baseUrl}/rooms/groups/all`)).data;
  },

  async updateRoomGroup(roomId: string, groupId: string) {
    await request.patch(`${baseUrl}/rooms/${roomId}/update-group`, { groupId });
  },

  async cancelRoom(roomId: string): Promise<NotaryAPI.RoomStatusResponse> {
    return (await request.post(`${baseUrl}/rooms/${roomId}/cancel`)).data;
  },

  async getAssignedOrganizations(): Promise<NotaryAPI.OrganizationsResponse> {
    return (await request.get(`${baseUrl}/rooms/organizations/assigned`)).data;
  },

  async fetchRoomMetadata(roomId: string): Promise<NotaryAPI.RoomMetadata> {
    return (await request.get(`${baseUrl}/rooms/${roomId}/metadata`)).data;
  },

  async fetchRoomNotes(roomId: string): Promise<NotaryAPI.NotesResponse> {
    return (await request.get(`${baseUrl}/rooms/${roomId}/notes?`)).data;
  },

  async createRoomNote(roomId: string, note: NotaryAPI.CreateNoteRequestBody) {
    await request.post(`${baseUrl}/rooms/${roomId}/notes`, note);
  },

  async search(searchInput: string, limit: number): Promise<NotaryAPI.RoomListResponse> {
    return (await request.get(`${baseUrl}/rooms/search`, { params: { searchInput, limit } })).data;
  },

  async fetchRoomNotaryTasks(roomId: string): Promise<NotaryAPI.NotaryTasksResponse[]> {
    return (await request.get(`${baseUrl}/notary-tasks`, { params: { roomId } })).data;
  },

  async updateRoomNotaryTask(taskId: string, task: NotaryAPI.UpdateNotaryTaskRequestBody) {
    await request.patch(`${baseUrl}/notary-tasks/${taskId}`, task);
  },

  async fetchEnvelopeReferences(roomId: string): Promise<DocumentsAPI.EnvelopeReference[]> {
    return (await request.get(`${baseUrl}/rooms/${roomId}/envelope-references`)).data;
  },
});
